import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import {
  integrationsFixture,
  saveIntegrationFixture,
  clearIntegrationFixture,
} from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('integrations');

/**
 * Spec: lmm-wiki/wiki/orbit-specs/admin-integrations-and-settings-shapes.md
 *
 * Core liefert `GET /v1/admin/integrations` als `{items, providers}`.
 * `items` enthält alles was die UI braucht — `providers` ist Metadata-Subset
 * und wird ignoriert.
 */
function normalizeIntegrationsList(raw) {
  dlog('raw', raw);
  if (!raw) return { items: [] };
  const items = raw.items ?? raw.providers ?? [];
  return { items: Array.isArray(items) ? items : [], raw };
}

const INVALIDATE = ['integrations'];

/** GET /v1/admin/integrations */
export function useIntegrations() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['integrations', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeIntegrationsList(integrationsFixture());
      }
      const res = await fetch('/v1/admin/integrations');
      return normalizeIntegrationsList(res);
    },
    staleTime: 30_000,
  });
}

/**
 * PUT /v1/admin/integrations/:provider
 *
 * Body-Shape (einheitlich für alle Provider):
 *   { config: {<fieldKey>: <value>, …}, notes?: "..." }
 *
 * Per Spec A3: Empty-String oder null auf einem Secret-Feld = "Wert unverändert
 * lassen". Non-Secret-Felder werden NICHT preserved (empty = clear).
 */
export function useSaveIntegration() {
  return useApiMutation({
    live: (fetch, { provider, config, notes }) =>
      fetch(`/v1/admin/integrations/${encodeURIComponent(provider)}`, {
        method: 'PUT',
        body: { config, notes: notes ?? null },
      }),
    fixture: ({ provider, config, notes }) =>
      saveIntegrationFixture(provider, { config, notes }),
    invalidate: INVALIDATE,
    successMessage: (_, { provider }) => `${provider}-Credentials gespeichert.`,
  });
}

/** DELETE /v1/admin/integrations/:provider — Provider-Row komplett entfernen. */
export function useClearIntegration() {
  return useApiMutation({
    live: (fetch, { provider }) =>
      fetch(`/v1/admin/integrations/${encodeURIComponent(provider)}`, { method: 'DELETE' }),
    fixture: ({ provider }) => clearIntegrationFixture(provider),
    invalidate: INVALIDATE,
    successMessage: (_, { provider }) => `${provider}-Credentials entfernt.`,
  });
}
